import { create } from 'zustand';
import { Workspace } from '@/types';
import {
  getUserWorkspaces,
  getAllWorkspaces,
  createWorkspace as dbCreateWorkspace,
  getWorkspaceById,
  getWorkspaceByIdAsSuperadmin,
  updateWorkspace as dbUpdateWorkspace,
  deleteWorkspace as dbDeleteWorkspace,
  checkWorkspaceAdmin,
  WorkspaceWithRole
} from '@/lib/database';
import { Tables } from '@/types/database.types';
import { supabase } from '@/lib/supabase';
import { useAuthStore } from './auth';

// Extended workspace type with membership and role info
interface WorkspaceWithMembership extends Workspace {
  is_member: boolean;
  role: 'admin' | 'reader' | 'superadmin' | null;
}

// Convert database workspace to our Workspace type
const convertDbWorkspace = (
  dbWorkspace: WorkspaceWithRole | Tables<'workspaces'>,
  is_member = true,
  role: 'admin' | 'reader' | 'superadmin' | null = null
): WorkspaceWithMembership => ({
  id: dbWorkspace.id,
  name: dbWorkspace.name,
  subdomain: dbWorkspace.subdomain,
  type: dbWorkspace.type,
  created_at: dbWorkspace.created_at || '',
  is_member,
  role,
});

// Persistence helpers
const WORKSPACE_STORAGE_KEY = 'ingesta-current-workspace';
const RECENT_WORKSPACES_KEY = 'ingesta-recent-workspaces';
const WORKSPACE_USER_KEY = 'ingesta-workspace-user-id';
const MAX_RECENT_WORKSPACES = 3;

// When a superadmin opens a project "as" a role in a new tab, that tab runs in
// an ephemeral impersonation mode. Because all tabs of the same origin share
// localStorage, any workspace write here would clobber the superadmin's real
// session in their other (panel) tab. This module-level flag short-circuits
// every workspace persistence write/removal for the impersonating tab's whole
// lifetime, keeping the assumed context purely in-memory.
let impersonationMode = false;

const saveWorkspaceToStorage = (workspace: Workspace | null, userId?: string) => {
  if (impersonationMode) return;
  try {
    if (workspace) {
      localStorage.setItem(WORKSPACE_STORAGE_KEY, JSON.stringify(workspace));
      if (userId) {
        localStorage.setItem(WORKSPACE_USER_KEY, userId);
      }
    } else {
      localStorage.removeItem(WORKSPACE_STORAGE_KEY);
      localStorage.removeItem(WORKSPACE_USER_KEY);
    }
  } catch (error) {
    console.warn('Failed to save workspace to localStorage:', error);
  }
};

const loadWorkspaceFromStorage = (currentUserId?: string): Workspace | null => {
  try {
    const storedUserId = localStorage.getItem(WORKSPACE_USER_KEY);
    const stored = localStorage.getItem(WORKSPACE_STORAGE_KEY);

    // If we have a current user ID and it doesn't match stored, clear and return null
    if (currentUserId && storedUserId && storedUserId !== currentUserId) {
      console.log('Clearing stale workspace data from different user');
      if (!impersonationMode) {
        localStorage.removeItem(WORKSPACE_STORAGE_KEY);
        localStorage.removeItem(WORKSPACE_USER_KEY);
        localStorage.removeItem(RECENT_WORKSPACES_KEY);
      }
      return null;
    }

    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.warn('Failed to load workspace from localStorage:', error);
    return null;
  }
};

const saveRecentWorkspaceIds = (ids: string[]) => {
  if (impersonationMode) return;
  try {
    localStorage.setItem(RECENT_WORKSPACES_KEY, JSON.stringify(ids.slice(0, MAX_RECENT_WORKSPACES)));
  } catch (error) {
    console.warn('Failed to save recent workspaces to localStorage:', error);
  }
};

const loadRecentWorkspaceIds = (): string[] => {
  try {
    const stored = localStorage.getItem(RECENT_WORKSPACES_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.warn('Failed to load recent workspaces from localStorage:', error);
    return [];
  }
};

const addToRecentWorkspaces = (workspaceId: string): string[] => {
  const recent = loadRecentWorkspaceIds();
  // Remove if already exists, then add to front
  const filtered = recent.filter(id => id !== workspaceId);
  const updated = [workspaceId, ...filtered].slice(0, MAX_RECENT_WORKSPACES);
  saveRecentWorkspaceIds(updated);
  return updated;
};

// Assumed role for superadmin access
type AssumedRole = 'admin' | 'reader' | null;

interface WorkspaceState {
  currentWorkspace: Workspace | null;
  workspaces: WorkspaceWithMembership[];
  recentWorkspaceIds: string[];
  isLoading: boolean;
  error: string | null;
  isAdmin: boolean;
  // Superadmin role assumption
  assumedRole: AssumedRole;
  assumedWorkspaceId: string | null;
  // True in a tab opened from the super panel to view a project "as" a role.
  // The tab holds its assumed context purely in-memory (see impersonationMode).
  impersonating: boolean;
  setCurrentWorkspace: (workspace: Workspace | null) => void;
  // Enter ephemeral impersonation for a superadmin-opened project tab: load the
  // target workspace and assume the given role, all in-memory, without touching
  // the shared localStorage session.
  enterImpersonation: (workspaceId: string, role: 'admin' | 'reader') => Promise<void>;
  setWorkspaces: (workspaces: WorkspaceWithMembership[]) => void;
  fetchWorkspaces: (isSuperadmin?: boolean) => Promise<void>;
  createWorkspace: (name: string, subdomain: string, description?: string) => Promise<Workspace>;
  selectWorkspace: (workspaceId: string, assumeRole?: AssumedRole) => Promise<void>;
  updateWorkspace: (workspaceId: string, updates: { name?: string; subdomain?: string; description?: string }) => Promise<void>;
  deleteWorkspace: (workspaceId: string) => Promise<void>;
  refreshCurrentWorkspace: () => Promise<void>;
  checkAdminStatus: (workspaceId: string) => Promise<void>;
  clearError: () => void;
  getRecentWorkspaces: () => WorkspaceWithMembership[];
  clearAssumedRole: () => void;
  getEffectiveRole: () => 'admin' | 'reader' | null;
  canEdit: () => boolean;
  clearWorkspace: () => void;
}

export const useWorkspaceStore = create<WorkspaceState>((set, get) => ({
  // Don't load from localStorage on init - wait for fetchWorkspaces to validate
  currentWorkspace: null,
  workspaces: [],
  recentWorkspaceIds: [],
  isLoading: false,
  error: null,
  isAdmin: false,
  assumedRole: null,
  assumedWorkspaceId: null,
  impersonating: false,

  enterImpersonation: async (workspaceId, role) => {
    // Flip the module flag first so any workspace write triggered during load
    // (or later while browsing the impersonated project) is suppressed — the
    // shared localStorage must keep pointing at the superadmin's real session.
    impersonationMode = true;
    set({ isLoading: true, error: null });
    try {
      const { isSuperadmin } = useAuthStore.getState();
      if (!isSuperadmin) {
        throw new Error('Only superadmins can open a project as another role');
      }
      const dbWorkspace = await getWorkspaceByIdAsSuperadmin(workspaceId);
      if (!dbWorkspace) {
        throw new Error('Workspace not found');
      }
      // Set the assumed context directly (not via setCurrentWorkspace, which
      // would persist to localStorage and run a real membership check).
      set({
        currentWorkspace: convertDbWorkspace(dbWorkspace),
        assumedRole: role,
        assumedWorkspaceId: workspaceId,
        isAdmin: role === 'admin',
        impersonating: true,
        isLoading: false,
      });
    } catch (error) {
      console.error('Failed to enter impersonation:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to open project',
        isLoading: false,
      });
      throw error;
    }
  },

  setCurrentWorkspace: (workspace) => {
    set({ currentWorkspace: workspace });
    // Get user ID from auth store for persistence
    const userId = useAuthStore.getState().user?.id;
    saveWorkspaceToStorage(workspace, userId);
    // Track recently used workspaces
    if (workspace?.id) {
      const recentIds = addToRecentWorkspaces(workspace.id);
      set({ recentWorkspaceIds: recentIds });
      // Check admin status when workspace is set
      get().checkAdminStatus(workspace.id);
    }
  },

  getRecentWorkspaces: () => {
    const { workspaces, recentWorkspaceIds } = get();
    return recentWorkspaceIds
      .map(id => workspaces.find(w => w.id === id))
      .filter((w): w is WorkspaceWithMembership => w !== undefined);
  },
  
  setWorkspaces: (workspaces) => {
    set({ workspaces });
  },

  clearError: () => {
    set({ error: null });
  },

  clearWorkspace: () => {
    // Clear all workspace state - used when switching users
    set({
      currentWorkspace: null,
      workspaces: [],
      recentWorkspaceIds: [],
      isLoading: false,
      error: null,
      isAdmin: false,
      assumedRole: null,
      assumedWorkspaceId: null,
    });
    // Clear localStorage (never from an impersonating tab — that storage
    // belongs to the superadmin's real session in their other tab).
    if (!impersonationMode) {
      localStorage.removeItem(WORKSPACE_STORAGE_KEY);
      localStorage.removeItem(RECENT_WORKSPACES_KEY);
      localStorage.removeItem(WORKSPACE_USER_KEY);
    }
  },

  fetchWorkspaces: async (isSuperadmin?: boolean) => {
    set({ isLoading: true, error: null });
    try {
      // Validate stored workspace belongs to current user
      const currentUserId = useAuthStore.getState().user?.id;
      const storedWorkspace = loadWorkspaceFromStorage(currentUserId);

      // Load recent workspace IDs if they were cleared
      const storedRecentIds = loadRecentWorkspaceIds();
      if (storedRecentIds.length > 0) {
        set({ recentWorkspaceIds: storedRecentIds });
      }

      let workspaces: WorkspaceWithMembership[];

      if (isSuperadmin) {
        // Superadmins see all workspaces with membership and role info
        const allWorkspaces = await getAllWorkspaces();
        workspaces = allWorkspaces.map(w => convertDbWorkspace(w, w.is_member, w.role));
      } else {
        // Regular users only see their own workspaces with their role
        const dbWorkspaces = await getUserWorkspaces();
        workspaces = dbWorkspaces.map(w => convertDbWorkspace(w, w.is_member, w.role));
      }

      set({ workspaces, isLoading: false });

      const { currentWorkspace: existingWorkspace } = get();

      // If no current workspace, try to restore from localStorage
      if (!existingWorkspace && storedWorkspace) {
        const storedWorkspaceExists = workspaces.find(w => w.id === storedWorkspace.id);
        if (storedWorkspaceExists) {
          console.log('Restoring workspace from localStorage:', storedWorkspace.name);
          get().setCurrentWorkspace(storedWorkspaceExists);
          return;
        } else {
          // Stored workspace not in user's workspaces - clear localStorage
          console.log('Stored workspace not accessible, clearing');
          saveWorkspaceToStorage(null);
        }
      }

      // Validate current workspace still exists and user has access
      if (existingWorkspace) {
        const workspaceStillExists = workspaces.find(w => w.id === existingWorkspace.id);
        if (!workspaceStillExists) {
          // Current workspace no longer accessible, clear it
          get().setCurrentWorkspace(null);
        }
      }

      // Auto-select the first workspace if no workspace is currently selected
      const { currentWorkspace: updatedCurrentWorkspace } = get();
      if (!updatedCurrentWorkspace && workspaces.length > 0) {
        get().setCurrentWorkspace(workspaces[0]);
      }
    } catch (error) {
      console.error('Failed to fetch workspaces:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to fetch workspaces',
        isLoading: false
      });
    }
  },
  
  createWorkspace: async (name: string, subdomain: string, description?: string) => {
    set({ isLoading: true, error: null });
    try {
      const dbWorkspace = await dbCreateWorkspace(name, subdomain, description);
      const newWorkspace = convertDbWorkspace(dbWorkspace);
      
      const { workspaces } = get();
      set({ 
        workspaces: [newWorkspace, ...workspaces],
        isLoading: false,
        isAdmin: true // Creator is always admin
      });
      get().setCurrentWorkspace(newWorkspace);
      
      return newWorkspace;
    } catch (error) {
      console.error('Failed to create workspace:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to create workspace';
      set({ 
        error: errorMessage,
        isLoading: false 
      });
      throw new Error(errorMessage);
    }
  },

  selectWorkspace: async (workspaceId: string, assumeRole?: AssumedRole) => {
    set({ isLoading: true, error: null });
    try {
      const { isSuperadmin } = useAuthStore.getState();

      let dbWorkspace;
      if (assumeRole && isSuperadmin) {
        // Superadmin accessing workspace with assumed role
        dbWorkspace = await getWorkspaceByIdAsSuperadmin(workspaceId);
        if (!dbWorkspace) {
          throw new Error('Workspace not found');
        }
        // Set the assumed role
        set({
          assumedRole: assumeRole,
          assumedWorkspaceId: workspaceId,
          isAdmin: assumeRole === 'admin'
        });
      } else {
        // Regular access - clear any assumed role
        dbWorkspace = await getWorkspaceById(workspaceId);
        if (!dbWorkspace) {
          throw new Error('Workspace not found or access denied');
        }
        set({ assumedRole: null, assumedWorkspaceId: null });
        // Check admin status normally
        await get().checkAdminStatus(workspaceId);
      }

      const workspace = convertDbWorkspace(dbWorkspace);
      set({ isLoading: false });
      get().setCurrentWorkspace(workspace);

    } catch (error) {
      console.error('Failed to select workspace:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to select workspace',
        isLoading: false
      });
      throw error;
    }
  },

  updateWorkspace: async (workspaceId: string, updates) => {
    set({ isLoading: true, error: null });
    try {
      const dbWorkspace = await dbUpdateWorkspace(workspaceId, updates);
      const updatedWorkspace = convertDbWorkspace(dbWorkspace);

      const { workspaces, currentWorkspace } = get();
      const newWorkspaces = workspaces.map(w =>
        w.id === workspaceId ? updatedWorkspace : w
      );

      set({
        workspaces: newWorkspaces,
        isLoading: false
      });

      // Update current workspace if it's the one being updated
      if (currentWorkspace?.id === workspaceId) {
        get().setCurrentWorkspace(updatedWorkspace);
      }

    } catch (error) {
      console.error('Failed to update workspace:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to update workspace';
      set({
        error: errorMessage,
        isLoading: false
      });
      throw new Error(errorMessage);
    }
  },

  deleteWorkspace: async (workspaceId: string) => {
    set({ isLoading: true, error: null });
    try {
      // Cancel any active Stripe subscription BEFORE the row delete cascades.
      // The cascade removes the subscriptions row but doesn't touch Stripe;
      // skipping this would silently leave the subscription billing the
      // customer. The edge function blocks on cancel failure (per F7) so
      // the user sees an error and retries rather than silent leakage.
      const { workspaces } = get();
      const workspace = workspaces.find(w => w.id === workspaceId);
      if (workspace) {
        const { error: cancelError } = await supabase.functions.invoke(
          'cancel-workspace-subscription',
          {
            body: {
              workspace_id: workspaceId,
              type: workspace.type,
            },
          },
        );
        if (cancelError) {
          throw new Error(
            `Could not cancel the workspace's Stripe subscription: ${cancelError.message}. ` +
              `Please try again, or contact support if the problem persists.`,
          );
        }
      }

      await dbDeleteWorkspace(workspaceId);

      const { currentWorkspace } = get();
      const newWorkspaces = get().workspaces.filter(w => w.id !== workspaceId);

      set({
        workspaces: newWorkspaces,
        isLoading: false
      });

      // Clear current workspace if it's the one being deleted
      if (currentWorkspace?.id === workspaceId) {
        get().setCurrentWorkspace(null);
      }

    } catch (error) {
      console.error('Failed to delete workspace:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete workspace';
      set({
        error: errorMessage,
        isLoading: false
      });
      throw new Error(errorMessage);
    }
  },

  refreshCurrentWorkspace: async () => {
    const { currentWorkspace } = get();
    if (!currentWorkspace) return;
    
    try {
      await get().selectWorkspace(currentWorkspace.id);
    } catch (error) {
      console.error('Failed to refresh current workspace:', error);
    }
  },

  checkAdminStatus: async (workspaceId: string) => {
    try {
      const { assumedRole, assumedWorkspaceId } = get();

      // If we have an assumed role for this workspace, use it
      if (assumedRole && assumedWorkspaceId === workspaceId) {
        set({ isAdmin: assumedRole === 'admin' });
        return;
      }

      // Otherwise check actual admin status
      const isAdmin = await checkWorkspaceAdmin(workspaceId);
      set({ isAdmin });
    } catch (error) {
      console.error('Failed to check admin status:', error);
      set({ isAdmin: false });
    }
  },

  clearAssumedRole: () => {
    set({ assumedRole: null, assumedWorkspaceId: null });
    // Re-check admin status for current workspace
    const { currentWorkspace } = get();
    if (currentWorkspace) {
      get().checkAdminStatus(currentWorkspace.id);
    }
  },

  getEffectiveRole: () => {
    const { assumedRole, assumedWorkspaceId, currentWorkspace, isAdmin } = get();

    // If assuming a role for the current workspace, return that
    if (assumedRole && assumedWorkspaceId === currentWorkspace?.id) {
      return assumedRole;
    }

    // Otherwise return actual role based on admin status
    return isAdmin ? 'admin' : 'reader';
  },

  canEdit: () => {
    const effectiveRole = get().getEffectiveRole();
    // Only admins can edit - readers are view-only
    return effectiveRole === 'admin';
  },
}));

// Convenience hook for common workspace operations
export const useWorkspace = () => {
  const store = useWorkspaceStore();
  return {
    currentWorkspace: store.currentWorkspace,
    workspaces: store.workspaces,
    isLoading: store.isLoading,
    error: store.error,
    isAdmin: store.isAdmin,
    assumedRole: store.assumedRole,
    assumedWorkspaceId: store.assumedWorkspaceId,
    impersonating: store.impersonating,
    enterImpersonation: store.enterImpersonation,
    fetchWorkspaces: store.fetchWorkspaces,
    createWorkspace: store.createWorkspace,
    selectWorkspace: store.selectWorkspace,
    updateWorkspace: store.updateWorkspace,
    clearError: store.clearError,
    clearAssumedRole: store.clearAssumedRole,
    getEffectiveRole: store.getEffectiveRole,
    canEdit: store.canEdit,
  };
};